import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloudIcon, XIcon, FileTextIcon, Loader2Icon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
import { PARTIES, ResponseStatus } from '../data';
interface UploadIntakeProps {
  onBack: () => void;
}
type Stage = 'config' | 'loading' | 'review';
interface ExtractedCard {
  id: string;
  number: string;
  question: string;
  response: string;
  objections: string;
  confidence: 'high' | 'amber';
  flags: ResponseStatus[];
}
const SAMPLE_EXTRACTED_DATA: ExtractedCard[] = [{
  id: '1',
  number: 'Form Interrogatory 2.1',
  question: 'State your full legal name, any former names, and your principal place of business.',
  response: 'Westfield Distribution LLC, a Delaware limited liability company with its principal place of business at 1420 Harbor Boulevard, Suite 300, Long Beach, California 90802. No former names.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '2',
  number: 'Form Interrogatory 6.1',
  question: 'Identify all documents that support your denials in your answer to the complaint.',
  response: 'See documents produced in Production Set No. 1, Bates Nos. WDL-000001 through WDL-000847.',
  objections: 'Responding party objects to this interrogatory as overly broad and unduly burdensome to the extent it seeks identification of every document supporting every denial.',
  confidence: 'amber',
  flags: ['Substantive']
}, {
  id: '3',
  number: 'Form Interrogatory 8.7',
  question: 'State the total income you have lost as a result of the incident described in the complaint, and the method of calculation you used to arrive at that figure.',
  response: 'Responding party has not yet completed its calculation of damages. Discovery and investigation are ongoing.',
  objections: 'Responding party objects to this interrogatory as premature and vague.',
  confidence: 'high',
  flags: ['Objection Only']
}, {
  id: '4',
  number: 'Special Interrogatory No. 1',
  question: 'Describe in detail all quality control testing performed on the Model X-400 unit between January 2023 and June 2023.',
  response: 'Quality control testing on the X-400 unit during the specified period included standard batch testing per ISO 9001 protocols, monthly visual inspections, and quarterly stress testing. Records of these tests were maintained by the QC department under the supervision of Director of Quality Assurance Michael Torres.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '5',
  number: 'Special Interrogatory No. 2',
  question: 'Identify every employee who participated in or had knowledge of discussions regarding a potential recall of the X-400 unit.',
  response: 'Michael Torres, Director of QA; Linda Park, VP of Operations; James Whitfield, General Counsel.',
  objections: 'Responding party objects to this interrogatory as overbroad and as seeking information protected by the attorney-client privilege to the extent it encompasses communications with counsel regarding potential recall. Without waiving said objections, Responding Party identifies the following individuals.',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '6',
  number: 'Special Interrogatory No. 3',
  question: 'State the date on which you first received notice of any complaint, claim, or lawsuit relating to the X-400 unit.',
  response: 'Responding party received its first written complaint regarding the X-400 unit on or about September 3, 2023, from a retail customer in Orange County, California.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '7',
  number: 'Special Interrogatory No. 4',
  question: 'Describe all modifications or design changes made to the X-400 unit after the date identified in your response to Special Interrogatory No. 3.',
  response: 'Following receipt of the initial complaint, the engineering team conducted a review and implemented a revised gasket seal design (Part No. GS-4401-R2) effective November 2023. See engineering change order ECO-2023-089, produced at Bates No. WDL-001204.',
  objections: 'Responding party objects to this interrogatory to the extent it calls for disclosure of proprietary engineering processes and trade secrets.',
  confidence: 'amber',
  flags: ['Substantive']
}, {
  id: '8',
  number: 'Special Interrogatory No. 5',
  question: 'State the total number of X-400 units sold or distributed between January 2023 and December 2023.',
  response: 'Approximately 12,800 units.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}];
const REVIEW_CATEGORIES: ('Substantive' | 'Objection Only')[] = ['Substantive', 'Objection Only'];
export function UploadIntake({
  onBack
}: UploadIntakeProps) {
  const [stage, setStage] = useState<Stage>('config');
  // Form state
  const [party, setParty] = useState(PARTIES[1]);
  const [setNumber, setSetNumber] = useState('Set 3');
  const [discoveryType, setDiscoveryType] = useState<'interrogatories' | 'rfa' | 'document'>('interrogatories');
  const [interrogatoryType, setInterrogatoryType] = useState<'form' | 'special'>('form');
  const [dateSent, setDateSent] = useState('');
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Review state
  const [cards, setCards] = useState<ExtractedCard[]>(SAMPLE_EXTRACTED_DATA);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  const handleProcess = () => {
    setStage('loading');
    setTimeout(() => {
      setStage('review');
    }, 1500);
  };
  const handleConfirm = () => {
    setShowSuccessToast(true);
    setTimeout(() => {
      onBack();
    }, 1500);
  };
  const updateCard = (id: string, field: keyof ExtractedCard, value: any) => {
    setCards(cards.map(c => c.id === id ? {
      ...c,
      [field]: value
    } : c));
  };
  const toggleFlag = (id: string, flag: ResponseStatus) => {
    setCards(cards.map(c => {
      if (c.id === id) {
        const newFlags = c.flags.includes(flag) ? c.flags.filter(f => f !== flag) : [...c.flags, flag];
        return {
          ...c,
          flags: newFlags
        };
      }
      return c;
    }));
  };
  const isFormValid = party && setNumber && dateReceived;
  return <div className="flex flex-col h-full bg-geniac-bg overflow-hidden relative" data-id="element-234">
      {/* Top Bar */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-geniac-border flex items-center justify-between" data-id="element-235">
        <button onClick={onBack} className="text-sm text-geniac-muted hover:text-geniac-text transition-colors" data-id="element-236">
          Cancel Upload
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-237">
        <AnimatePresence mode="wait" data-id="element-238">
          {/* STAGE 1: CONFIG */}
          {stage === 'config' && <motion.div key="config" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="max-w-2xl mx-auto py-12 px-6" data-id="element-239">
              <p className="text-xs text-geniac-muted mb-2" data-id="element-240">
                Martinez v. Acme Corp et al. — Case No. 2023-CV-04817
              </p>
              <h1 className="text-xl font-semibold text-geniac-text mb-8" data-id="element-241">
                Add Discovery Responses
              </h1>

              <div className="space-y-6" data-id="element-242">
                {/* Discovery Type */}
                <div data-id="element-243">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-244">
                    Discovery Type
                  </label>
                  <div className="flex gap-3" data-id="element-245">
                    <button onClick={() => setDiscoveryType('interrogatories')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${discoveryType === 'interrogatories' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-246">
                      Interrogatories
                    </button>
                    <button onClick={() => setDiscoveryType('rfa')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${discoveryType === 'rfa' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-247">
                      Requests for Admission
                    </button>
                    <button onClick={() => setDiscoveryType('document')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${discoveryType === 'document' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-248">
                      Document Requests
                    </button>
                  </div>
                </div>

                {/* Party & Set */}
                <div className="grid grid-cols-3 gap-4" data-id="element-249">
                  <div className="col-span-2" data-id="element-250">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-251">
                      Responding Party
                    </label>
                    <select value={party} onChange={e => setParty(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent" data-id="element-252">
                      {PARTIES.map(p => <option key={p} value={p} data-id="element-253">
                          {p}
                        </option>)}
                      <option value="new" className="text-geniac-accent font-medium" data-id="element-254">
                        + Add new party
                      </option>
                    </select>
                  </div>
                  <div data-id="element-255">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-256">
                      Set Number
                    </label>
                    <input type="text" value={setNumber} onChange={e => setSetNumber(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent" data-id="element-257" />
                  </div>
                </div>

                {/* Interrogatory Type (only for interrogatories) */}
                {discoveryType === 'interrogatories' && <div data-id="element-258">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-259">
                      Interrogatory Type
                    </label>
                    <div className="flex gap-3" data-id="element-260">
                      <button onClick={() => setInterrogatoryType('form')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${interrogatoryType === 'form' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-261">
                        Form Interrogatories
                      </button>
                      <button onClick={() => setInterrogatoryType('special')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${interrogatoryType === 'special' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-262">
                        Special Interrogatories
                      </button>
                    </div>
                  </div>}

                {/* Date Sent & Date Received */}
                <div className="grid grid-cols-2 gap-4" data-id="element-263">
                  <div data-id="element-264">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-265">
                      Date Sent
                    </label>
                    <input type="date" value={dateSent} onChange={e => setDateSent(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent [color-scheme:dark]" data-id="element-266" />
                  </div>
                  <div data-id="element-267">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-268">
                      Date Received
                    </label>
                    <input type="date" value={dateReceived} onChange={e => setDateReceived(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent [color-scheme:dark]" data-id="element-269" />
                  </div>
                </div>

                {/* Drag and Drop Zone */}
                <div className="pt-4" data-id="element-270">
                  <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf" className="hidden" data-id="element-271" />

                  {!file ? <div onDragOver={e => e.preventDefault()} onDrop={handleFileDrop} onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-geniac-border hover:border-geniac-accent/50 rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors bg-geniac-panel/30" data-id="element-272">
                      <UploadCloudIcon className="w-10 h-10 text-geniac-muted mb-4" data-id="element-273" />
                      <p className="text-sm text-geniac-text font-medium" data-id="element-274">
                        Drag and drop response PDF here, or click to browse
                      </p>
                      <p className="text-xs text-geniac-muted mt-2" data-id="element-275">
                        Supports PDF up to 50MB
                      </p>
                    </div> : <div className="border border-geniac-border rounded-xl p-4 flex items-center justify-between bg-geniac-panel" data-id="element-276">
                      <div className="flex items-center gap-3" data-id="element-277">
                        <div className="p-2 bg-geniac-bg rounded-lg border border-geniac-border" data-id="element-278">
                          <FileTextIcon className="w-6 h-6 text-geniac-accent" data-id="element-279" />
                        </div>
                        <div data-id="element-280">
                          <p className="text-sm font-medium text-geniac-text" data-id="element-281">
                            {file.name}
                          </p>
                          <p className="text-xs text-geniac-muted" data-id="element-282">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setFile(null)} className="p-2 text-geniac-muted hover:text-geniac-text hover:bg-geniac-hover rounded-md transition-colors" data-id="element-283">
                        <XIcon className="w-5 h-5" data-id="element-284" />
                      </button>
                    </div>}
                </div>

                {/* Submit */}
                <div className="pt-6" data-id="element-285">
                  <button onClick={handleProcess} disabled={!isFormValid} className="w-full py-3 rounded-lg bg-geniac-accent text-geniac-bg font-semibold text-sm transition-all hover:bg-geniac-accent/90 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-286">
                    Process Document
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* STAGE 2: LOADING */}
          {stage === 'loading' && <motion.div key="loading" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center h-full" data-id="element-287">
              <Loader2Icon className="w-10 h-10 text-geniac-accent animate-spin mb-4" data-id="element-288" />
              <p className="text-geniac-text font-medium" data-id="element-289">
                Extracting interrogatory responses...
              </p>
              <p className="text-sm text-geniac-muted mt-2" data-id="element-290">
                This usually takes a few seconds.
              </p>
            </motion.div>}

          {/* STAGE 3: REVIEW */}
          {stage === 'review' && <motion.div key="review" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-4xl mx-auto py-10 px-6 pb-32" data-id="element-291">
              <div className="mb-8" data-id="element-292">
                <h1 className="text-xl font-semibold text-geniac-text" data-id="element-293">
                  Review Extracted Responses
                </h1>
                <p className="text-sm text-geniac-muted mt-1" data-id="element-294">
                  We found {cards.length} interrogatory responses in this
                  document. Please review and correct any errors before
                  confirming.
                </p>
              </div>

              <div className="space-y-6" data-id="element-295">
                {cards.map(card => <div key={card.id} className="bg-geniac-panel border border-geniac-border rounded-lg overflow-hidden" data-id="element-296">
                    {/* Card Header */}
                    <div className="px-5 py-3.5 border-b border-geniac-border flex items-center justify-between bg-geniac-hover" data-id="element-297">
                      <div className="flex items-center gap-3" data-id="element-298">
                        <span className="flex items-center justify-center w-7 h-7 rounded-md bg-geniac-accent/15 text-geniac-accent text-xs font-bold tabular-nums" data-id="element-299">
                          {String(cards.indexOf(card) + 1).padStart(2, '0')}
                        </span>
                        <input type="text" value={card.number} onChange={e => updateCard(card.id, 'number', e.target.value)} className="text-base font-bold text-geniac-text bg-transparent border-b border-transparent focus:border-geniac-accent focus:outline-none px-1 py-0.5 w-64" data-id="element-300" />
                      </div>
                      <div className="flex items-center gap-2" data-id="element-301">
                        <div className={`w-2 h-2 rounded-full ${card.confidence === 'high' ? 'bg-emerald-400' : 'bg-amber-400'}`} data-id="element-302" />
                        <span className={`text-xs font-medium ${card.confidence === 'high' ? 'text-emerald-400' : 'text-amber-400'}`} data-id="element-303">
                          {card.confidence === 'high' ? 'High confidence' : 'Review recommended'}
                        </span>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 space-y-4" data-id="element-304">
                      <div data-id="element-305">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-1.5" data-id="element-306">
                          Question
                        </label>
                        <textarea value={card.question} onChange={e => updateCard(card.id, 'question', e.target.value)} rows={2} className="w-full bg-geniac-bg border border-geniac-border rounded-md px-3 py-2 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent resize-y" data-id="element-307" />
                      </div>

                      <div data-id="element-308">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-1.5" data-id="element-309">
                          Response
                        </label>
                        <textarea value={card.response} onChange={e => updateCard(card.id, 'response', e.target.value)} rows={4} className="w-full bg-geniac-bg border border-geniac-border rounded-md px-3 py-2 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent resize-y" data-id="element-310" />
                      </div>

                      <div data-id="element-311">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-1.5" data-id="element-312">
                          Objections
                        </label>
                        <textarea value={card.objections} onChange={e => updateCard(card.id, 'objections', e.target.value)} placeholder="No objections detected." rows={2} className="w-full bg-geniac-bg border border-geniac-border rounded-md px-3 py-2 text-sm text-geniac-text placeholder-geniac-muted focus:outline-none focus:ring-1 focus:ring-geniac-accent resize-y" data-id="element-313" />
                      </div>
                    </div>

                    {/* Card Footer - Category */}
                    <div className="px-5 py-3 border-t border-geniac-border bg-geniac-bg/50 flex items-center gap-2" data-id="element-314">
                      <span className="text-xs text-geniac-muted mr-1" data-id="element-315">
                        Category:
                      </span>
                      {REVIEW_CATEGORIES.map(cat => {
                  const isActive = card.flags.includes(cat);
                  const activeStyle = cat === 'Substantive' ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50' : 'bg-rose-900/30 text-rose-400 border-rose-800/50';
                  return <button key={cat} onClick={() => updateCard(card.id, 'flags', [cat])} className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${isActive ? activeStyle : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text hover:border-geniac-muted/50'}`} data-id="element-316">
                            {cat}
                          </button>;
                })}
                    </div>
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Sticky Bottom Action Bar for Review Stage */}
      {stage === 'review' && <div className="absolute bottom-0 left-0 right-0 border-t border-geniac-border bg-geniac-panel/95 backdrop-blur-sm px-6 py-4 flex items-center justify-between z-10" data-id="element-317">
          <button onClick={onBack} className="px-4 py-2 text-sm font-medium text-geniac-muted border border-geniac-border rounded-lg hover:text-geniac-text hover:bg-geniac-hover transition-colors" data-id="element-318">
            Cancel
          </button>
          <button onClick={handleConfirm} className="px-6 py-2 text-sm font-semibold bg-geniac-accent text-geniac-bg rounded-lg hover:bg-geniac-accent/90 transition-colors shadow-lg shadow-geniac-accent/20" data-id="element-319">
            Confirm and Add to Case
          </button>
        </div>}

      {/* Success Toast */}
      <AnimatePresence data-id="element-320">
        {showSuccessToast && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-emerald-900/90 border border-emerald-800/50 text-emerald-100 px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50 backdrop-blur-md" data-id="element-321">
            <CheckCircle2Icon className="w-5 h-5 text-emerald-400" data-id="element-322" />
            <span className="text-sm font-medium" data-id="element-323">
              Successfully added {cards.length} responses to case.
            </span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}